import { ActionContext, ActionTree, Module, MutationTree } from 'vuex';
import { RootState } from '../../utils/types';
import { AcceptedConsent, LegalEntityConsent, MigrateResponse, MigrationState, MigrationStatus, MissingConsentByLEID } from './migration.types';
import { Http } from '@pirates/not-morty-biz';
import { migrationEndpoints } from './migration.config';
import userStore from '../../store/user/user.store';
import { Vue } from 'vue-property-decorator';
import { loadLegalEntityMigrationConsents, loadTermsAndConditions, shareNowLogout } from './migration.service';
import { t } from '@/utils/translation';
import { sentry } from '@/utils/sentry';
import { Severity } from '@sentry/types';

export const captureMigrationError = (err: Error, action: string, severity = Severity.Error, obj?: any) => {
  sentry.withScope(function (scope) {
    scope.setTags({ isDNmigration: 'true' });
    scope.setExtra('action', action);
    scope.setExtra('obj', obj);
    scope.setLevel(severity);

    if (err) {
      sentry.captureException(err);
    } else {
      sentry.captureMessage('Something went wrong');
    }
  });
};
class Migration implements Module<MigrationState, RootState> {
  public state: MigrationState = {
    allMigrationConsentsByLegalEntityId: {},
    termsAndConditions: {},
    missingConsents: [],
    optionalConsents: [],
    isMigrated: false,
  };
  public namespaced: boolean = true;

  public actions = {
    async migrateUser(context: ActionContext<MigrationState, RootState>) {
      try {
        const userMigrationStatus: { status: MigrationStatus } = await Http.post(migrationEndpoints.migrate, {});
        // user is ready to migrate
        if (userMigrationStatus) {
          const status = userMigrationStatus.status;
          if (status === 'IMPORT_COMPLETED') {
            context.commit('SET_USER_IS_MIGRATED');
          } else if (status === 'ALREADY_MIGRATED') {
            context.commit('SET_USER_IS_MIGRATED');
            Vue.utils.$toast(t('account_already_migrated').toString(), 'warn', false);
            setTimeout(() => {
              shareNowLogout();
            }, 5000);
          }
        }
      } catch (err: any) {
        const { status } = err.response;
        if (status === 412) {
          const data: MigrateResponse = err.response.data;
          // Filter LE IDs to fetch consents once
          const allConsents = data.MISSING_CONSENTS.concat(data.OPTIONAL_CONSENTS);
          const allLegalEnitytConsents: LegalEntityConsent[] = [];
          if (allConsents.length) {
            const alllegalEntityIds: number[] = [];
            const allMigrationConsentsByLegalEntityId: MissingConsentByLEID = {};
            allConsents.forEach((consentItem) => {
              alllegalEntityIds.push(consentItem.legalEntityId);
              allMigrationConsentsByLegalEntityId[consentItem.consentSlug] = consentItem;
            });
            context.commit('SET_MISSING_CONSENTS_BY_LE_ID', allMigrationConsentsByLegalEntityId);

            const uniqueLegalEntityIds = [...new Set(alllegalEntityIds)]; // array of unique LE IDs
            for (const legalEntityId of uniqueLegalEntityIds) {
              try {
                const legalEntityMigrationConsents = await loadLegalEntityMigrationConsents(legalEntityId);
                if (legalEntityMigrationConsents && legalEntityMigrationConsents.length) {
                  allLegalEnitytConsents.push(...legalEntityMigrationConsents);
                }
              } catch (err) {
                captureMigrationError(err as Error, 'migration.store/migrateUser/@loadLegalEntityMigrationConsents', Severity.Error, { legalEntityId: legalEntityId });
              }
            }
          }
          // At this point All LE Consents are available as FE DB for consents
          // case user is not migrated -- MISSING TNC OR CONSENTS
          if (data.MISSING_CONSENTS.length) {
            const missingConsents = data.MISSING_CONSENTS.map((missingConsent) => {
              return allLegalEnitytConsents.find((legalEntityConsent) => {
                return legalEntityConsent.slug === missingConsent.consentSlug;
              });
            });
            context.commit('SET_MISSING_CONSENTS', missingConsents);
          }

          if (data.OPTIONAL_CONSENTS.length) {
            const optionalConsents = data.OPTIONAL_CONSENTS.map((optionalConsent) => {
              return allLegalEnitytConsents.find((legalEntityConsent) => {
                return legalEntityConsent.slug === optionalConsent.consentSlug;
              });
            });
            context.commit('SET_OPTIONAL_CONSENTS', optionalConsents);
          }

          if (data.MISSING_TNCS.length) {
            for (const legalEntityTnCId of data.MISSING_TNCS) {
              /* This will be always a loop over one element as we have single LE ID for TnCs
              If in the future this changes, this should be easy to change here, and for sake
              of scalability this TnCs will be stored as an object of LE ID and the TnC Object
              */

              try {
                const termsAndConditions = await loadTermsAndConditions();
                context.commit('SET_MIGRATION_TNC', { termsAndConditions, legalEntityId: legalEntityTnCId });
              } catch (errTNC) {
                captureMigrationError(err, 'migration.store/migrateUser/@loadTermsAndConditions', Severity.Error, { legalEntityId: legalEntityTnCId });
                context.dispatch('app/showServerError', errTNC);
              }
            }
          }
        } else if (status === 409) {
          // case email conflict
          captureMigrationError(err, 'migration.store/migrateUser@email-conflict', Severity.Debug);
          Vue.utils.$toast(t('email_conflict').toString(), 'warn', false);
        } else {
          captureMigrationError(err, 'migration.store/migrateUser');
          context.dispatch('app/showServerError', err);
        }
      }
    },

    async acceptTnC(context: ActionContext<MigrationState, RootState>, termsAndConditions: { [legalEntityId: number]: { isAccepted: boolean } }) {
      for (const legalEntityId of Object.keys(termsAndConditions)) {
        const tnc: { isAccepted: boolean } = termsAndConditions[legalEntityId];
        try {
          if (tnc.isAccepted) {
            await Http.put(migrationEndpoints.acceptTnC(parseInt(legalEntityId)), {});
          }
        } catch (err) {
          captureMigrationError(err as Error, 'migration.store/acceptTnC');
          context.dispatch('app/showServerError', err);
        }
      }
    },

    async acceptConsents(context: ActionContext<MigrationState, RootState>, allMigrationConsents: { [consentSlug: string]: AcceptedConsent }) {
      const userUUID = userStore.state.user.uuid;
      for (const consentSlug in allMigrationConsents) {
        const consent = allMigrationConsents[consentSlug];
        if (consent.granted) {
          try {
            await Http.patch(migrationEndpoints.acceptConsent(userUUID, consentSlug), consent);
          } catch (err) {
            captureMigrationError(err as Error, 'migration.store/acceptConsents');
            context.dispatch('app/showServerError', err);
          }
        }
      }
    },
  } as ActionTree<MigrationState, RootState>;

  public mutations = {
    SET_MISSING_CONSENTS(state: MigrationState, missingConsents: LegalEntityConsent[]) {
      state.missingConsents = missingConsents;
    },

    SET_OPTIONAL_CONSENTS(state: MigrationState, optionalConsents: LegalEntityConsent[]) {
      state.optionalConsents = optionalConsents;
    },

    SET_MIGRATION_TNC(state: MigrationState, termsAndConditionsPayload: any) {
      const { termsAndConditions, legalEntityId } = termsAndConditionsPayload;
      const { keys } = termsAndConditions;
      const {
        fields: { slug, text },
      } = keys[0];
      Vue.set(state.termsAndConditions, legalEntityId, { slug, text });
    },

    SET_MISSING_CONSENTS_BY_LE_ID(state: MigrationState, allMigrationConsentsByLegalEntityId: MissingConsentByLEID) {
      state.allMigrationConsentsByLegalEntityId = allMigrationConsentsByLegalEntityId;
    },

    SET_USER_IS_MIGRATED(state: MigrationState) {
      state.isMigrated = true;
    },
  } as MutationTree<MigrationState>;
}

export default new Migration();
